/**
 * Adobe After Effects → Webout data converters.
 *
 * Two independent parsers for the two AE export formats used in the platform:
 *
 * 1. **Corner Pin** (`parseAeCornerPin` + `toWeboutDynamic` / `toWeboutStatic`)
 *    Converts a full 4-corner AE Corner Pin export into webout `placement` data.
 *    Used in content-item-card to drive where a content layer sits in 2-D space.
 *
 * 2. **Single-point keyframes** (`parseAeKeyframes`)
 *    Converts a single-corner (or any single X/Y property) AE keyframe export
 *    into a flat keyframe array. Used in scene-form to animate action positions.
 */

// ── Shared types ─────────────────────────────────────────────────────────────

interface FramePoint {
	frame: number;
	x: number;
	y: number;
}

interface Coord {
	x: number;
	y: number;
}

// ── Corner Pin ────────────────────────────────────────────────────────────────

interface ParsedCornerPin {
	upperLeft: FramePoint[];
	upperRight: FramePoint[];
	lowerLeft: FramePoint[];
	lowerRight: FramePoint[];
}

/**
 * Parse an Adobe AE Corner Pin keyframe export.
 *
 * Expected input: the raw text copied from AE's "Copy keyframe data" for a
 * Corner Pin effect. The text contains 4 labelled sections — one per corner —
 * each with tab-separated rows of `Frame  X pixels  Y pixels`.
 *
 * Rows with negative frame numbers are silently discarded (AE sometimes
 * exports pre-composition frames that precede frame 0).
 */
export function parseAeCornerPin(text: string): ParsedCornerPin {
	const result: ParsedCornerPin = {
		upperLeft: [],
		upperRight: [],
		lowerLeft: [],
		lowerRight: [],
	};

	// Split into sections by the "Effects" header lines
	const sections = text.split(/Effects\s+Corner Pin #\d+\s+/i);

	for (const section of sections) {
		const trimmed = section.trim();
		if (!trimmed) continue;

		let target: FramePoint[] | null = null;
		if (/^Upper Left/i.test(trimmed)) target = result.upperLeft;
		else if (/^Upper Right/i.test(trimmed)) target = result.upperRight;
		else if (/^Lower Left/i.test(trimmed)) target = result.lowerLeft;
		else if (/^Lower Right/i.test(trimmed)) target = result.lowerRight;
		if (!target) continue;

		// Parse rows; skip the "Frame  X pixels  Y pixels" column header
		const lines = trimmed.split('\n');
		for (const line of lines) {
			const clean = line.trim();
			if (!clean || /^(Upper|Lower|Frame)/i.test(clean)) continue;
			// Handles both tab and multi-space separators
			const parts = clean.split(/\t+|\s{2,}/).map(s => s.trim()).filter(Boolean);
			if (parts.length >= 3) {
				const frame = parseInt(parts[0], 10);
				const x = parseFloat(parts[1]);
				const y = parseFloat(parts[2]);
				if (Number.isFinite(frame) && frame >= 0 && Number.isFinite(x) && Number.isFinite(y)) {
					target.push({ frame, x, y });
				}
			}
		}
	}

	return result;
}

/**
 * Convert parsed Corner Pin data to webout **dynamic** coords format.
 *
 * Output shape: `{ "0": { coords: [UL, UR, LL, LR] }, "1": { ... }, ... }`
 * Assign to `placement.frames`. Frames where any corner is missing are skipped.
 */
export function toWeboutDynamic(parsed: ParsedCornerPin): Record<string, { coords: Coord[] }> {
	const frames = new Set<number>();
	for (const corner of [parsed.upperLeft, parsed.upperRight, parsed.lowerLeft, parsed.lowerRight]) {
		for (const p of corner) frames.add(p.frame);
	}

	const ulMap = new Map(parsed.upperLeft.map(p => [p.frame, p]));
	const urMap = new Map(parsed.upperRight.map(p => [p.frame, p]));
	const llMap = new Map(parsed.lowerLeft.map(p => [p.frame, p]));
	const lrMap = new Map(parsed.lowerRight.map(p => [p.frame, p]));

	const result: Record<string, { coords: Coord[] }> = {};
	for (const frame of [...frames].sort((a, b) => a - b)) {
		const ul = ulMap.get(frame);
		const ur = urMap.get(frame);
		const ll = llMap.get(frame);
		const lr = lrMap.get(frame);
		if (ul && ur && ll && lr) {
			result[String(frame)] = {
				coords: [
					{ x: ul.x, y: ul.y },
					{ x: ur.x, y: ur.y },
					{ x: ll.x, y: ll.y },
					{ x: lr.x, y: lr.y },
				],
			};
		}
	}

	return result;
}

/**
 * Convert parsed Corner Pin data to webout **static** coords format (frame 0 only).
 *
 * Returns `[UL, UR, LL, LR]` — assign directly to `placement.coords`.
 * Falls back to `{x:0, y:0}` for any corner missing at frame 0.
 */
export function toWeboutStatic(parsed: ParsedCornerPin): Coord[] {
	const ul = parsed.upperLeft.find(p => p.frame === 0);
	const ur = parsed.upperRight.find(p => p.frame === 0);
	const ll = parsed.lowerLeft.find(p => p.frame === 0);
	const lr = parsed.lowerRight.find(p => p.frame === 0);

	return [
		{ x: ul?.x ?? 0, y: ul?.y ?? 0 },
		{ x: ur?.x ?? 0, y: ur?.y ?? 0 },
		{ x: ll?.x ?? 0, y: ll?.y ?? 0 },
		{ x: lr?.x ?? 0, y: lr?.y ?? 0 },
	];
}

const CORNER_PIN_NAMES = ['Upper Left', 'Upper Right', 'Lower Left', 'Lower Right'] as const;

/**
 * Inverse of `toWeboutDynamic` / `toWeboutStatic`: render webout placement
 * coords as AE Corner Pin keyframe data, ready to paste onto a Corner Pin effect.
 *
 * Uses `placement.frames` when present, otherwise `placement.coords` as frame 0.
 * AE refuses to paste without the header, and the platform does not store the
 * comp's fps or size, so the caller has to supply them.
 */
export interface AeComp {
	fps: number;
	width: number;
	height: number;
}

export function toAeCornerPin(
	placement: { coords?: Coord[]; frames?: Record<string, { coords: Coord[] }> },
	comp: AeComp,
): string {
	const frames: Array<[number, Coord[]]> = placement.frames
		? Object.entries(placement.frames).map(([f, v]): [number, Coord[]] => [Number(f), v.coords]).sort((a, b) => a[0] - b[0])
		: [[0, placement.coords ?? []]];

	const lines = [
		'Adobe After Effects 8.0 Keyframe Data',
		'',
		`\tUnits Per Second\t${comp.fps}`,
		`\tSource Width\t${comp.width}`,
		`\tSource Height\t${comp.height}`,
		'\tSource Pixel Aspect Ratio\t1',
		'\tComp Pixel Aspect Ratio\t1',
		'',
	];
	CORNER_PIN_NAMES.forEach((name, i) => {
		const rows = frames.filter(([, coords]) => coords[i]).map(([frame, coords]) => `\t${frame}\t${coords[i].x}\t${coords[i].y}`);
		if (rows.length === 0) return;
		lines.push(`Effects\tCorner Pin #1\t${name}`, '\tFrame\tX pixels\tY pixels', ...rows, '');
	});
	lines.push('', 'End of Keyframe Data');
	return lines.join('\n');
}

/**
 * Inverse of the action import: actions store only the top-left corner plus a
 * size, so the other corners are rebuilt as an axis-aligned rectangle (any
 * rotation the original corner pin had is lost on import). Without a size only
 * Upper Left is emitted, which the single-point import reads back.
 */
export function actionToAeCornerPin(
	action: { coords?: Coord; frames?: Record<string, { coords: Coord }>; size?: { width: number; height: number } },
	comp: AeComp,
): string {
	const corners = ({ x, y }: Coord): Coord[] => {
		if (!action.size) return [{ x, y }];
		const { width: w, height: h } = action.size;
		return [{ x, y }, { x: x + w, y }, { x, y: y + h }, { x: x + w, y: y + h }];
	};
	if (action.frames) {
		const frames = Object.fromEntries(Object.entries(action.frames).map(([f, v]) => [f, { coords: corners(v.coords) }]));
		return toAeCornerPin({ frames }, comp);
	}
	return toAeCornerPin({ coords: action.coords ? corners(action.coords) : [] }, comp);
}

/**
 * Compute a rectangular button's top-left position + size from 3 of its 4
 * corner-pin points (PRO-192). Uses Euclidean corner-to-corner distance
 * rather than a plain axis delta — robust to the slight rotation/perspective
 * a hand-placed corner pin often has, not just perfectly axis-aligned boxes.
 */
export function cornerPinToButtonRect(corners: { upperLeft: Coord; upperRight: Coord; lowerLeft: Coord }): { x: number; y: number; width: number; height: number } {
	const width = Math.hypot(corners.upperRight.x - corners.upperLeft.x, corners.upperRight.y - corners.upperLeft.y);
	const height = Math.hypot(corners.lowerLeft.x - corners.upperLeft.x, corners.lowerLeft.y - corners.upperLeft.y);
	return { x: corners.upperLeft.x, y: corners.upperLeft.y, width, height };
}

export interface CornerPinButtonKeyframes {
	/** Size computed once, from the first complete frame. */
	rect: { x: number; y: number; width: number; height: number };
	/** Top-left position per frame that has all 3 required corners. */
	frames: Array<{ frame: number; x: number; y: number }>;
}

/**
 * Reconciles parsed corner-pin keyframes into what scene-form's action
 * import needs: the frames where Upper Left/Right/Lower Left all share a
 * frame number (Lower Right isn't needed for the rect calc), plus the
 * button rect computed once from the earliest such frame. Returns `null`
 * when no frame has all 3 required corners.
 */
export function cornerPinToButtonKeyframes(parsed: ParsedCornerPin): CornerPinButtonKeyframes | null {
	const allFrames = [...new Set(parsed.upperLeft.map(p => p.frame))].sort((a, b) => a - b);
	const ulMap = new Map(parsed.upperLeft.map(p => [p.frame, p]));
	const urMap = new Map(parsed.upperRight.map(p => [p.frame, p]));
	const llMap = new Map(parsed.lowerLeft.map(p => [p.frame, p]));

	const completeFrames = allFrames.filter(f => ulMap.has(f) && urMap.has(f) && llMap.has(f));
	if (completeFrames.length === 0) return null;

	const firstUl = ulMap.get(completeFrames[0]);
	const firstUr = urMap.get(completeFrames[0]);
	const firstLl = llMap.get(completeFrames[0]);
	if (!firstUl || !firstUr || !firstLl) return null;

	const rect = cornerPinToButtonRect({ upperLeft: firstUl, upperRight: firstUr, lowerLeft: firstLl });
	const frames: Array<{ frame: number; x: number; y: number }> = [];
	for (const f of completeFrames) {
		const ul = ulMap.get(f);
		if (ul) frames.push({ frame: f, x: ul.x, y: ul.y });
	}

	return { rect, frames };
}

// ── Single-point keyframes ────────────────────────────────────────────────────

/**
 * Parse a single-property AE keyframe export (one X/Y point per frame).
 *
 * Expected input: the raw text from AE's "Copy keyframe data" for a single
 * property (e.g. one corner, or a position). The section must contain a
 * `Frame\tX pixels\tY pixels` header row followed by tab-separated data rows.
 *
 * Rows with negative frame numbers are silently discarded.
 *
 * Returns a flat array of `{ frame, x, y }` sorted in input order.
 * Returns `[]` if the header row is not found.
 */
export interface AeKeyframesResult {
	keyframes: Array<{ frame: number; x: number; y: number }>;
	/** Number of rows discarded because their frame number was negative. */
	skippedNegative: number;
}

export function parseAeKeyframes(text: string): AeKeyframesResult {
	const lines = text.split('\n').map(l => l.trim()).filter(Boolean);
	const headerIdx = lines.findIndex(l => l.startsWith('Frame') && l.includes('X pixels'));
	if (headerIdx === -1) return { keyframes: [], skippedNegative: 0 };

	const keyframes: Array<{ frame: number; x: number; y: number }> = [];
	let skippedNegative = 0;

	for (let i = headerIdx + 1; i < lines.length; i++) {
		const line = lines[i];
		if (line === 'End of Keyframe Data') break;
		// Stop at the next section header (handles multi-corner AE exports)
		if (line.startsWith('Frame') && line.includes('X pixels')) break;
		const parts = line.split('\t');
		const frame = parseFloat(parts[0]);
		const x = parseFloat(parts[1]);
		const y = parseFloat(parts[2]);
		if (isNaN(frame) || isNaN(x) || isNaN(y)) continue;
		if (frame < 0) { skippedNegative++; continue; }
		keyframes.push({ frame, x, y });
	}
	return { keyframes, skippedNegative };
}
